import { useCallback, useEffect } from 'react';
import { useField, useForm } from 'react-final-form';
import { Box } from '@mui/material';
import { TextField as TextFieldRFF } from 'mui-rff';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { get } from 'lodash';

import { countriesSelectors } from '../../redux/countriesSlice';
import AddressLookup from '../AddressLookup';
import {
  formatCoordinates,
  transformAddressValues,
} from '../../helpers/address';
import CountriesSelect from '../CountriesSelect';
import { fastFind } from '../../apis/address';
import { StringUtil } from '../../utils';
import { FormInput } from '../FormInput';

const AddressDetailsForm = ({
  sx,
  disabled,
  config: {
    fields,
    inputSize = 'medium',
    idPrefix,
    availableCountryCodes = [],
    addressBookConfig = {},
  },
  validatePostcodeOnCountryChange = false,
}) => {
  const form = useForm();

  const { values } = form.getState();
  const { addressBookType } = addressBookConfig;
  const countryISO = get(values, fields.country.name);
  const addressLookupKey = get(values, fields.addressId.name);
  const countries = useSelector(countriesSelectors.getCountries);
  const availableCountries = availableCountryCodes.map(countryCode =>
    countries.find(({ countryKey }) => countryCode === countryKey)
  );
  const { input: countrySelectInput } = useField(fields.country.name);

  const shouldAutoFill = fullAddress => {
    // User selected new address from the search lookup
    if (fullAddress?.isOptionSelectedManually) {
      return true;
    }

    // Postcode change
    return get(values, fields.postcode.name) !== fullAddress.Postcode;
  };

  const handleAddressChange = () => {
    if (!addressBookType || !get(values, addressBookType)) {
      return;
    }
    form.change(addressBookType, undefined);
  };

  const clearAddressDetailsForm = () => {
    form.batch(() => {
      Object.keys(fields).forEach(field => {
        if (fields[field].canResetOption) {
          form.change(fields[field].name, null);
        }
      });
    });
  };

  // TODO
  const onAddressLookupChange = useCallback(
    fullAddress => {
      if (!shouldAutoFill(fullAddress)) {
        return;
      }

      const { addressLine1, addressLine2 } = transformAddressValues({
        street: fullAddress.Street,
        locality: fullAddress.Locality,
        property: fullAddress.Property,
      });

      form.batch(() => {
        const fieldsToUpdate = [
          { name: fields.addressId.name, value: fullAddress.Key },
          {
            name: fields.coordinates.name,
            value: formatCoordinates(fullAddress),
          },
          {
            name: fields.street.name,
            value: addressLine1 || null,
          },
          {
            name: fields.locality.name,
            value: addressLine2 || null,
          },
          {
            name: fields.town.name,
            value: fullAddress.Town || null,
          },
          {
            name: fields.county.name,
            value: fullAddress.PostalCounty || null,
          },
          {
            name: fields.postcode.name,
            value:
              countryISO === 'US'
                ? fullAddress.Postcode?.slice(0, 5)
                : fullAddress.Postcode,
          },
          {
            name: fields.organisation.name,
            value: fullAddress.Organisation,
          },
        ];

        fieldsToUpdate.forEach(({ name, value }) => {
          form.change(name, value);
          form.blur(name);
        });
      });

      handleAddressChange();
    },
    [shouldAutoFill, form, fields, countryISO, handleAddressChange]
  );

  // TODO
  const findAndUpdateAddressLookupKey = async (
    lookup,
    countryCode = countryISO
  ) => {
    const { Item: results = [] } = await fastFind({
      lookup,
      countryISO: countryCode,
    });
    if (results.length > 0) {
      // Update address lookup key if it is found
      // debouncedOnChange(
      //   { ...formValues, [FIELDS.ADDRESS_KEY.KEY]: results.Item[0].Key },
      //   true
      // );
    }
  };

  useEffect(() => {
    !addressLookupKey &&
      values.postcode &&
      findAndUpdateAddressLookupKey(values.postcode);
  }, []);

  // Remove country select field since it have a special logic
  const regularFields = Object.keys(fields).filter(
    field => field !== 'country'
  );

  const onKeyDownHandler = event => {
    const fieldName = event.target.name;

    // User manual input additional handling
    const userInputHandlingMap = {
      [fields.postcode.name]: () => {
        // Cleanup Address input on manual postcode change
        form.change(fields.addressId.name, '');
      },
    };

    userInputHandlingMap[fieldName]?.(event);
  };

  const handleCountryChange = event => {
    const { value: countryValue } = event.target;

    countrySelectInput.onChange(countryValue);

    // trigger postcode validation after country change
    if (validatePostcodeOnCountryChange) {
      form.blur(fields.postcode.name);
    }

    if (countryValue !== countryISO) {
      clearAddressDetailsForm();
    }
  };

  return (
    <Box
      noValidate
      autoComplete='off'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ...sx,
      }}
    >
      <CountriesSelect
        component={TextFieldRFF}
        countries={availableCountries.length ? availableCountries : countries}
        blurOnSelect
        select
        label={fields.country.label}
        variant='outlined'
        required={fields.country.required}
        disabled={!countries.length || disabled || fields.country.disabled}
        name={fields.country.name}
        size={inputSize}
        onChange={handleCountryChange}
        autoComplete='off'
        inputProps={{
          'data-testid': `${idPrefix}-country-select`,
        }}
        slotProps={{
          inputLabel: { shrink: !!countrySelectInput.value || undefined },
        }}
        value={get(values, fields.country.name)}
      />

      <AddressLookup
        onChange={onAddressLookupChange}
        variant='outlined'
        addressKey={addressLookupKey}
        countryISO={countryISO}
        disabled={disabled}
        inputSize={inputSize}
        autocompleteInputId={`${idPrefix}-address-lookup`}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {regularFields.map(field => (
          <FormInput
            name={fields[field].name}
            key={fields[field].name}
            label={fields[field].label}
            onKeyDown={onKeyDownHandler}
            size={inputSize}
            disabled={disabled || fields[field].disabled}
            required={fields[field].required}
            inputProps={{
              'data-testid': `${idPrefix}-${field}`,
              'aria-required': fields[field].required,
            }}
            maxLength={fields[field].maxLength}
            fieldProps={{
              formatOnBlur: true,
              format: StringUtil.trim,
              parse: value =>
                fields[field].parse
                  ? fields[field].parse(value)
                  : StringUtil.trimFirstSpace(value),
            }}
            autoComplete='on'
            {...fields[field].props}
          />
        ))}
      </Box>
    </Box>
  );
};

const PropTypeFieldShape = PropTypes.shape({
  name: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
});

AddressDetailsForm.propTypes = {
  sx: PropTypes.object,
  disabled: PropTypes.bool,
  validatePostcodeOnCountryChange: PropTypes.bool,
  config: PropTypes.shape({
    fields: PropTypes.shape({
      // addressId: PropTypeFieldShape,
      country: PropTypeFieldShape,
      organisation: PropTypeFieldShape,
      street: PropTypeFieldShape,
      locality: PropTypeFieldShape,
      town: PropTypeFieldShape,
      county: PropTypeFieldShape,
    }),
    inputSize: PropTypes.string,
    hasActionButtons: PropTypes.bool,
    idPrefix: PropTypes.string,
  }),
};

export default AddressDetailsForm;
